/** Фиксация по расчёту производства: экран одной фиксации (прогресс с опросом → предпросмотр →
 *  итог) и список прошлых фиксаций. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";

import {
  discardFixation,
  type ConfirmResult,
  type Fixation,
  type FixationSummary,
  type PreviewRow,
} from "@/api/purchasePrices";
import { Button } from "@/components/ui/button";
import {
  dateRu,
  dateTimeRu,
  FIXATION_KIND_LABEL,
  FIXATION_STATUS_STYLE,
  FLAG_ORDER,
  FLAG_STYLE,
} from "./format";
import { PreviewPanel } from "./PreviewPanel";
import { KEY, useFixation, useFixations } from "./queries";
import { Badge, ErrorBox, Loading } from "./ui";

function Params({ f }: { f: FixationSummary }): React.ReactElement {
  return (
    <span>
      {FIXATION_KIND_LABEL[f.kind]}: расчёт на {dateRu(f.calc_date)}, действует с {dateRu(f.valid_from)},
      тираж {f.qty}
    </span>
  );
}

export function FixationView({ id }: { id: number }): React.ReactElement {
  const qc = useQueryClient();
  const q = useFixation(id);
  const [confirmed, setConfirmed] = useState<ConfirmResult | null>(null);
  const discard = useMutation({
    mutationFn: () => discardFixation(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY.fixation(id) });
      await qc.invalidateQueries({ queryKey: KEY.fixations });
    },
    onError: () => qc.invalidateQueries({ queryKey: KEY.fixation(id) }),
  });

  if (q.isLoading) return <Loading text="Загружаю фиксацию…" />;
  const f = q.data;
  if (!f) {
    return (
      <div className="space-y-2">
        <ErrorBox error={q.error} what={`Фиксация ${id} не загрузилась`} />
        <Button size="sm" variant="outline" onClick={() => void q.refetch()}>
          Повторить
        </Button>
      </div>
    );
  }
  const st = FIXATION_STATUS_STYLE[f.status];
  const canDiscard = (f.status === "running" || f.status === "preview") && confirmed === null;

  return (
    <div className="space-y-3" data-testid="fixation-view">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold">Фиксация {f.id}</span>
        <Badge cls={st.cls}>{st.label}</Badge>
        <span className="text-muted-foreground">
          <Params f={f} /> · запустил {f.created_by}, {dateTimeRu(f.created_at)}
        </span>
        {canDiscard && (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto h-7"
            disabled={discard.isPending}
            onClick={() => discard.mutate()}
          >
            Отменить фиксацию
          </Button>
        )}
      </div>
      {discard.isError && <ErrorBox error={discard.error} what="Фиксация не отменена" />}

      {/* опрос сорвался (502 — связь с производством): опрос остановлен, данные — прошлого ответа */}
      {q.isError && (
        <div className="space-y-2">
          <ErrorBox error={q.error} what="Опрос фиксации не удался" />
          <Button size="sm" variant="outline" onClick={() => void q.refetch()}>
            Опросить снова
          </Button>
        </div>
      )}

      {confirmed ? (
        <ConfirmResultView result={confirmed} rows={f.preview?.rows ?? []} />
      ) : (
        <StatusBody f={f} onConfirmed={setConfirmed} />
      )}
    </div>
  );
}

function StatusBody({
  f,
  onConfirmed,
}: {
  f: Fixation;
  onConfirmed: (r: ConfirmResult) => void;
}): React.ReactElement {
  switch (f.status) {
    case "running":
      return (
        <Loading
          text={
            "Портал производства считает себестоимость каталога" +
            (f.progress ? `: ${f.progress.done} из ${f.progress.total}` : "…") +
            " Обычно ~5 минут; прогресс подтянется при следующем открытии страницы «Себестоимость»."
          }
        />
      );
    case "failed":
      return (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400"
        >
          Фиксация не удалась: {f.error ?? "причина не записана"}
        </div>
      );
    case "discarded":
      return <p className="text-sm text-muted-foreground">Фиксация отброшена — цены не записаны.</p>;
    case "confirmed":
      if (!f.confirm_result) {
        return (
          <div role="alert" className="text-sm text-red-700 dark:text-red-400">
            Фиксация подтверждена, но итог подтверждения не сохранён — сообщите разработчику.
          </div>
        );
      }
      return (
        <ConfirmResultView
          result={f.confirm_result}
          rows={f.preview?.rows ?? []}
          by={f.confirmed_by}
          at={f.confirmed_at}
        />
      );
    case "preview":
      if (!f.preview) {
        return (
          <div role="alert" className="text-sm text-red-700 dark:text-red-400">
            Статус «предпросмотр», но строк предпросмотра нет — ответ бэкенда не по контракту.
          </div>
        );
      }
      return <PreviewPanel fixationId={f.id} preview={f.preview} validFrom={f.valid_from} onConfirmed={onConfirmed} />;
  }
}

function ConfirmResultView({
  result,
  rows,
  by,
  at,
}: {
  result: ConfirmResult;
  rows: PreviewRow[];
  by?: string | null;
  at?: string | null;
}): React.ReactElement {
  const byKey = new Map(rows.map((r) => [r.key, r]));
  return (
    <div className="space-y-2" data-testid="confirm-result">
      <p className="text-sm">
        Записано цен: <b>{result.written}</b>; пропущено: <b>{result.skipped.length}</b>
        {by && (
          <span className="text-muted-foreground">
            {" "}
            · подтвердил {by}, {dateTimeRu(at)}
          </span>
        )}
      </p>
      {result.skipped.length > 0 && (
        <div className="max-h-[50vh] overflow-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 font-medium">Артикул</th>
                <th className="px-2 py-1.5 font-medium">Название</th>
                <th className="px-2 py-1.5 font-medium">Причина пропуска</th>
              </tr>
            </thead>
            <tbody>
              {result.skipped.map((s) => {
                const r = byKey.get(s.key);
                return (
                  <tr key={s.key} className="border-t" data-testid="skipped-row">
                    <td className="px-2 py-1">{r?.article ?? "—"}</td>
                    <td className="px-2 py-1">{r?.name ?? <code className="text-xs">{s.key}</code>}</td>
                    <td className="px-2 py-1">{s.reason}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function FixationsList({ onOpen }: { onOpen: (id: number) => void }): React.ReactElement {
  const q = useFixations();
  if (q.isLoading) return <Loading text="Загружаю фиксации…" />;
  if (q.isError) return <ErrorBox error={q.error} what="Список фиксаций не загрузился" />;
  const items = q.data ?? [];
  if (items.length === 0) {
    return <p className="rounded-md border px-4 py-6 text-sm text-muted-foreground">Фиксаций ещё не было.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm" data-testid="fixations-table">
        <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
          <tr>
            <th className="px-2 py-2 font-medium">№</th>
            <th className="px-2 py-2 font-medium">Параметры</th>
            <th className="px-2 py-2 font-medium">Статус</th>
            <th className="px-2 py-2 font-medium">Запустил</th>
            <th className="px-2 py-2 font-medium">Итог</th>
            <th className="px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {items.map((f) => (
            <tr key={f.id} className="border-t align-top" data-testid="fixation-row">
              <td className="px-2 py-1.5 tabular-nums">{f.id}</td>
              <td className="px-2 py-1.5">
                <Params f={f} />
              </td>
              <td className="px-2 py-1.5">
                <Badge cls={FIXATION_STATUS_STYLE[f.status].cls}>{FIXATION_STATUS_STYLE[f.status].label}</Badge>
                {f.error && <div className="mt-0.5 max-w-80 text-xs text-red-700 dark:text-red-400">{f.error}</div>}
              </td>
              <td className="px-2 py-1.5 text-xs">
                {f.created_by}, {dateTimeRu(f.created_at)}
                {f.confirmed_by && (
                  <div className="text-muted-foreground">
                    подтвердил {f.confirmed_by}, {dateTimeRu(f.confirmed_at)}
                  </div>
                )}
              </td>
              <td className="px-2 py-1.5 text-xs">
                {f.confirm_result
                  ? `записано ${f.confirm_result.written}, пропущено ${f.confirm_result.skipped.length}`
                  : f.counts
                    ? FLAG_ORDER.filter((k) => f.counts?.[k])
                        .map((k) => `${FLAG_STYLE[k].label} ${f.counts?.[k]}`)
                        .join(", ")
                    : ""}
              </td>
              <td className="px-2 py-1.5 text-right">
                <Button size="sm" variant="outline" className="h-7" onClick={() => onOpen(f.id)}>
                  Открыть
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Незавершённая фиксация (идёт расчёт или ждёт подтверждения) — плашкой над вкладками. */
export function ActiveFixationBanner({ onOpen }: { onOpen: (id: number) => void }): React.ReactElement | null {
  const q = useFixations();
  if (q.isError) {
    return (
      <div className="space-y-2" data-testid="active-fixation-error">
        <ErrorBox error={q.error} what="Состояние фиксаций не загрузилось" />
        <Button size="sm" variant="outline" onClick={() => void q.refetch()}>
          Повторить
        </Button>
      </div>
    );
  }
  const active = q.data?.find((f) => f.status === "running" || f.status === "preview");
  if (!active) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300"
      data-testid="active-fixation"
    >
      Фиксация {active.id} ({FIXATION_KIND_LABEL[active.kind]}) —{" "}
      {active.status === "running" ? "идёт расчёт" : "ждёт подтверждения"}.
      <Button size="sm" variant="outline" className="h-7" onClick={() => onOpen(active.id)}>
        Открыть
      </Button>
    </div>
  );
}
